import type { FormEvent } from "react";

import { Button, LanguageSelect, Tooltip } from "@/atoms";
import { strings } from "@/constants";
import {
    ButtonTypeEnum,
    ButtonVariantEnum,
    CodePageStatusEnum,
    InputTypeEnum,
} from "@/enums";
import type { CodePageStatusType, LanguageType } from "@/types";

const buttonTextHandler = (status: CodePageStatusType): string => {
    if (status === CodePageStatusEnum.LOADING) return strings.code.buttonLoading;

    if (status === CodePageStatusEnum.ERROR) return strings.code.buttonReenter;

    return strings.code.buttonConfirm;
};

export const CodeForm = ({
    code,
    isCodeValid,
    lang,
    onChangeCode,
    onChangeLang,
    onReset,
    onSubmit,
    status,
}: {
    code: string,
    isCodeValid: boolean,
    lang: LanguageType,
    onChangeCode: (value: string) => void,
    onChangeLang: (value: LanguageType) => void,
    onReset: () => void,
    onSubmit: (event: FormEvent<HTMLFormElement>) => void,
    status: CodePageStatusType,
}) => {
    const isError = status === CodePageStatusEnum.ERROR;

    const isLoading = status === CodePageStatusEnum.LOADING;

    return (
        <form
            className="
                flex
                flex-col
                items-center
                justify-center
                gap-4
                sm:flex-row
            "
            onSubmit={onSubmit}
        >
            <div
                className={`
                    flex
                    w-full
                    max-w-lg
                    items-center
                    gap-2
                    rounded-full
                    border-2
                    bg-white
                    px-4
                    py-3
                    ${isError ? "border-red-500" : "border-primary"}
                `}
            >
                <input
                    disabled={isLoading}
                    placeholder={strings.code.inputPlaceholder}
                    type={InputTypeEnum.TEXT}
                    value={code}
                    className="
                        flex-1
                        bg-transparent
                        text-base
                        text-text-primary
                        outline-none
                        placeholder:text-gray-400
                        disabled:cursor-not-allowed
                        disabled:opacity-50
                    "
                    onChange={({ target }) => onChangeCode(target.value)}
                />
                <LanguageSelect
                    isDisabled={isLoading}
                    value={lang}
                    onChange={onChangeLang}
                />
            </div>
            <Tooltip
                content={strings.code.codeLength}
                isDisabled={isCodeValid || isError || isLoading}
            >
                <Button
                    disabled={isLoading || (status === CodePageStatusEnum.IDLE && !isCodeValid)}
                    isLoading={isLoading}
                    type={isError ? ButtonTypeEnum.BUTTON : ButtonTypeEnum.SUBMIT}
                    variant={isError || isCodeValid ? ButtonVariantEnum.PRIMARY : ButtonVariantEnum.MUTED}
                    onClick={isError ? onReset : undefined}
                >
                    {buttonTextHandler(status)}
                </Button>
            </Tooltip>
        </form>
    );
};
